<template>
  <v-container fluid grid-list-md class="pa-3">
    <v-layout row wrap align-center class="mb-2">
      <v-flex xs12 sm7>
        <div class="v2-section-title">Super Syndicate Reporting</div>
        <div class="v2-muted body-1">
          <v-icon small class="mr-1">verified_user</v-icon>Permission based view · Connected to LendWizely
        </div>
      </v-flex>
      <v-flex xs12 sm5 md3 offset-md2>
        <v-select :items="accountOptions" item-text="syndicate_abbreviation" item-value="id" v-model="selectedAccount" @change="onAccountChange" label="Syndicate Account" outline dense hide-details></v-select>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex v-for="card in statCards" :key="card.label" xs6 sm6 md3>
        <v-card flat class="v2-card v2-kpi">
          <v-layout row align-center justify-space-between>
            <div>
              <div class="v2-kpi-label">{{ card.label }}</div>
              <div class="v2-kpi-value">
                <template v-if="card.value === null || card.value === undefined">—</template>
                <template v-else-if="card.currency">{{ card.value | currency }}</template>
                <template v-else>{{ card.value }}</template>
              </div>
            </div>
            <v-avatar size="40" tile :color="tone(card.tone)[0]" class="v2-icon-box">
              <v-icon :color="tone(card.tone)[1]">{{ card.icon }}</v-icon>
            </v-avatar>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 lg8>
        <v-card flat class="v2-card">
          <v-card-title>
            <div>
              <div>Accounts Breakdown</div>
              <div class="v2-muted body-1 font-weight-regular">Each child syndicate account with profit, payout, and balance to date.</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn small depressed color="success" :loading="exportingBreakdown" @click="exportBreakdown"><v-icon small left>grid_on</v-icon>Excel</v-btn>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex v-for="item in accountBreakdown" :key="item.account" xs12 md6>
                <v-card flat class="v2-card v2-panel pa-3">
                  <v-layout row justify-space-between align-start class="mb-2">
                    <div>
                      <div class="font-weight-bold">{{ item.account }}</div>
                      <div class="caption v2-muted">{{ item.deals }} deals</div>
                    </div>
                    <v-chip v-if="item.status" small :color="statusTone(item.status).bg" :text-color="statusTone(item.status).text">{{ item.status }}</v-chip>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex v-for="field in accountCardFields(item)" :key="field.label" xs6 class="mb-2">
                      <div class="v2-kpi-label">{{ field.label }}</div>
                      <div class="font-weight-bold" :class="field.className">
                        <template v-if="field.value === null || field.value === undefined">—</template>
                        <template v-else>{{ field.value | currency }}</template>
                      </div>
                    </v-flex>
                  </v-layout>
                  <v-layout row justify-space-between class="caption font-weight-bold v2-muted">
                    <span>Total Payouts against Expected RTR</span><span>{{ percentCollected(item) }}%</span>
                  </v-layout>
                  <v-progress-linear :value="percentCollected(item)" color="success" height="6" class="my-1"></v-progress-linear>
                </v-card>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 lg4>
        <v-card flat class="v2-card">
          <v-card-title>
            <div>
              <div>Payouts</div>
              <div class="v2-muted body-1 font-weight-regular">Open a payout to view its transactions.</div>
            </div>
          </v-card-title>
          <v-progress-linear :indeterminate="loadingPayouts" height="3" class="my-0"></v-progress-linear>
          <v-card-text class="v2-payout-list">
            <v-card v-for="(item, i) in payouts" :key="i" flat class="v2-card v2-panel pa-3 mb-2 v2-row" @dblclick.native="openDialog(item)">
              <v-layout row justify-space-between align-center>
                <div>
                  <div v-if="item.syndicate_name" class="font-weight-bold">{{ item.syndicate_name }}</div>
                  <div class="caption v2-muted">{{ item.pay_date }}</div>
                </div>
                <div class="text-xs-right">
                  <div class="font-weight-bold success--text">{{ item.sum_net_pay | currency }}</div>
                  <div class="caption v2-muted">Gross {{ item.sum_gross_amt | currency }} · Fee {{ item.sum_mgt_fee | currency }}</div>
                </div>
              </v-layout>
              <v-layout row justify-end class="mt-1">
                <v-btn flat small class="ma-0" @click.stop="exportPayoutSheet(item)"><v-icon small left>file_download</v-icon>Download</v-btn>
                <v-btn flat small color="primary" class="ma-0" @click.stop="openDialog(item)"><v-icon small left>open_in_new</v-icon>Transactions</v-btn>
              </v-layout>
            </v-card>
            <div v-if="!loadingPayouts && payouts.length === 0" class="v2-muted body-1 text-xs-center pa-3">No payouts found.</div>
            <v-btn v-if="page < lastPage" block outline color="primary" :loading="loadingPayouts" @click="loadMore">Load more</v-btn>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-card flat class="v2-card">
      <v-card-title>
        <div>
          <div>Consolidated Portfolio Report</div>
          <div class="v2-muted body-1 font-weight-regular">All syndicated deals for the selected account.</div>
        </div>
        <v-spacer></v-spacer>
        <v-text-field v-model="search" @input="onSearch" append-icon="search" placeholder="Search syndicate, company, MID, status" outline hide-details single-line class="v2-portfolio-search mr-2"></v-text-field>
        <v-btn small depressed color="success" :loading="exportingPortfolio" @click="exportPortfolio"><v-icon small left>file_download</v-icon>Excel</v-btn>
      </v-card-title>
      <v-progress-linear :indeterminate="loadingPortfolio" height="3" class="my-0"></v-progress-linear>
      <div class="v2-table-scroll">
        <v-data-table :headers="headers" :items="rows" :total-items="totalItems" :loading="loadingPortfolio" :rows-per-page-items="[25, 50, 100, 200, { text: 'All', value: -1 }]" :pagination.sync="pagination">
          <template slot="items" slot-scope="props">
            <td class="font-weight-bold hidden-xs-only">{{ dash(props.item.syndicate_name) }}</td>
            <td>{{ dash(props.item.company_name) }}</td>
            <td class="hidden-xs-only">{{ dash(props.item.contract_MID) }}</td>
            <td class="hidden-xs-only">{{ formatDate(props.item.funded_date) }}</td>
            <td class="hidden-xs-only">{{ props.item.capital_participated | currency }}</td>
            <td class="hidden-xs-only">{{ props.item.participation_percent }}%</td>
            <td class="hidden-xs-only">{{ dash(props.item.factor_rate) }}</td>
            <td class="hidden-xs-only">{{ props.item.part_rtr | currency }}</td>
            <td class="hidden-xs-only">{{ props.item.collected | currency }}</td>
            <td>{{ props.item.remaining | currency }}</td>
            <td class="hidden-xs-only">{{ props.item.total_payout | currency }}</td>
            <td class="primary--text hidden-xs-only">{{ props.item.projected_profit | currency }}</td>
            <td class="success--text">{{ props.item.actual_profit | currency }}</td>
            <td><v-chip small :color="statusTone(props.item.status).bg" :text-color="statusTone(props.item.status).text">{{ dash(props.item.status) }}</v-chip></td>
            <td class="hidden-xs-only">
              <v-chip v-if="has(props.item.pace)" small :color="paceTone(props.item.pace).bg" :text-color="paceTone(props.item.pace).text">{{ props.item.pace }}</v-chip>
              <span v-else>-</span>
            </td>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <v-dialog v-model="dialog" max-width="1100" scrollable>
      <v-card>
        <v-card-title>
          Payouts<span v-if="selectedItem.pay_date" class="v2-muted body-1 ml-2">({{ selectedItem.pay_date }})</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-progress-linear :indeterminate="loadingTable" height="3" class="my-0"></v-progress-linear>
        <v-card-text class="pa-0" style="max-height: 70vh;">
          <v-data-table :headers="txHeaders" :items="transactions" hide-actions>
            <template slot="items" slot-scope="props">
              <td>{{ props.item.contract_MID }}</td>
              <td>{{ props.item.syndication_company_name }}</td>
              <td>{{ props.item.total_gross_amount | currency }}</td>
              <td>{{ props.item.total_mgt_fee | currency }}</td>
              <td class="font-weight-bold">{{ props.item.total_net_pay | currency }}</td>
              <td>{{ props.item.numbers_of_transactions }}</td>
            </template>
          </v-data-table>
          <div v-if="!loadingTable && transactions.length === 0" class="v2-muted body-1 text-xs-center pa-3">No transactions found.</div>
          <div v-if="transactions.length < txTotal" class="text-xs-center pa-2">
            <v-btn small outline color="primary" :loading="loadingTable" @click="loadMoreTransactions">Load more</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import user_helper from '../../helpers/user_helper'
import superSyndicate from '../mixins/superSyndicate'

export default {
  mixins: [user_helper, superSyndicate],
  data: () => ({
    headers: [
      { text: 'Syndicate Name', value: 'syndicate_name', class: 'hidden-xs-only' },
      { text: 'Company Name', value: 'company_name' },
      { text: 'MID', value: 'contract_MID', class: 'hidden-xs-only' },
      { text: 'Funded Date', value: 'funded_date', class: 'hidden-xs-only' },
      { text: 'Capital Participated', value: 'capital_participated', class: 'hidden-xs-only' },
      { text: 'Participation %', value: 'participation_percent', class: 'hidden-xs-only' },
      { text: 'Factor Rate', value: 'factor_rate', class: 'hidden-xs-only' },
      { text: 'Part RTR', value: 'part_rtr', class: 'hidden-xs-only' },
      { text: 'Collected', value: 'collected', sortable: false, class: 'hidden-xs-only' },
      { text: 'Remaining Balance', value: 'remaining' },
      { text: 'Total Payout', value: 'total_payout', sortable: false, class: 'hidden-xs-only' },
      { text: 'Projected Profit', value: 'projected_profit', sortable: false, class: 'hidden-xs-only' },
      { text: 'Actual Profit', value: 'actual_profit', sortable: false },
      { text: 'Status', value: 'status' },
      { text: 'Pace', value: 'pace', sortable: false, class: 'hidden-xs-only' },
    ],
    txHeaders: [
      { text: 'MID', sortable: false },
      { text: 'Company Name', sortable: false },
      { text: 'Gross Amount', sortable: false },
      { text: 'Mgt Fee', sortable: false },
      { text: 'Net Pay', sortable: false },
      { text: 'No. of Transactions', sortable: false },
    ],
  }),
}
</script>

<style scoped>
.v2-payout-list { max-height: 560px; overflow-y: auto; }
.v2-portfolio-search { max-width: 320px; }
.v2-icon-box { border-radius: 10px !important; }
</style>
